/**
 * Light and dark themes from the existing tokens: `.dark` on <html> switches every semantic
 * token in tokens.css. The person's choice is remembered on this device; "system" follows the
 * operating system and updates when it changes.
 */
import { useCallback, useEffect, useState } from 'react';
import { readDeviceSetting, writeDeviceSetting } from './device-setting';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'inferrex.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function readThemePreference(): ThemePreference {
  const raw = readDeviceSetting(THEME_STORAGE_KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Put the theme on the document. `light` is set too, so tokens.css's system query cannot override it. */
export function applyTheme(theme: ResolvedTheme, root: HTMLElement | undefined = typeof document !== 'undefined' ? document.documentElement : undefined): void {
  if (!root) return;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.style.colorScheme = theme;
}

export function useThemePreference(): { preference: ThemePreference; resolved: ResolvedTheme; setPreference: (p: ThemePreference) => void } {
  const [preference, setPref] = useState<ThemePreference>(readThemePreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  useEffect(() => {
    const apply = () => {
      const next = resolveTheme(preference);
      setResolved(next);
      applyTheme(next);
    };
    apply();
    if (preference !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    writeDeviceSetting(THEME_STORAGE_KEY, p);
  }, []);

  return { preference, resolved, setPreference };
}
